import axios from "axios";

export const hasYoutubeRoundChanges = (
  current: { round: string; status: string; email: string; link: string; provider?: string },
  next: { round: string; status: string; email: string; link: string; provider?: string },
) =>
  current.round !== next.round ||
  current.status !== next.status ||
  current.email !== next.email ||
  current.link !== next.link ||
  current.provider !== next.provider;

export const fetchMemberExtrainfo = async (partyid: number, uid: number) => {
  if (!partyid) {
    throw new Error("파티 ID가 없어 추가 정보를 불러올 수 없습니다.");
  }

  for (const memberState of [0, 1, 2]) {
    const response = await axios.get(
      `/api/party/${partyid}/users/${memberState}?page=0&size=100`,
    );
    const member = response.data?.items?.find(
      (item: { uid: number | string }) => Number(item.uid) === uid,
    );
    if (member) {
      const raw = member.extrainfo;
      const parsed = typeof raw === "string" ? JSON.parse(raw || "{}") : raw ?? {};
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        throw new Error("추가 정보 형식이 올바르지 않습니다.");
      }
      return parsed as Record<string, unknown>;
    }
  }

  throw new Error("파티에서 해당 이용자의 추가 정보를 찾지 못했습니다.");
};

export const saveYoutubeExtrainfo = async ({
  partyid,
  uid,
  roundResult,
  ytstate,
  duedate,
}: {
  partyid: number;
  uid: number;
  roundResult?: { round: number; r_status: number };
  ytstate?: number;
  duedate?: string;
}) => {
  const currentExtra = await fetchMemberExtrainfo(partyid, uid);
  const { r_account: _oldRoundAccount, ...existingExtra } = currentExtra;
  if (
    !Object.prototype.hasOwnProperty.call(currentExtra, "r_account") &&
    roundResult === undefined &&
    ytstate === undefined &&
    duedate === undefined
  ) {
    return currentExtra;
  }
  const nextExtra = {
    ...existingExtra,
    ...(roundResult && { round: roundResult.round, r_status: roundResult.r_status }),
    ...(ytstate !== undefined && { ytstate }),
    ...(duedate !== undefined && { duedate }),
  };
  await axios.post("/api/party/updateInfo", {
    partyid,
    uid,
    extrainfo: JSON.stringify(nextExtra),
    reason: "extrainfo_change",
  });
  const savedExtra = await fetchMemberExtrainfo(partyid, uid);
  if (
    Object.prototype.hasOwnProperty.call(savedExtra, "r_account") ||
    (roundResult !== undefined && Number(savedExtra.round) !== roundResult.round) ||
    (roundResult !== undefined && Number(savedExtra.r_status) !== roundResult.r_status) ||
    (ytstate !== undefined && Number(savedExtra.ytstate) !== ytstate) ||
    (duedate !== undefined && savedExtra.duedate !== duedate)
  ) {
    throw new Error("추가 정보가 저장되지 않았습니다. 다시 확인해 주세요.");
  }
  return savedExtra;
};

export const saveYoutubeUrlAllocation = async ({
  pid,
  uid,
  email,
  round,
  r_status,
  r_link,
  provider,
}: {
  pid: string;
  uid: string;
  email: string;
  round: number;
  r_status: number;
  r_link: string;
  provider?: string;
}) => {
  const response = await axios.post("/api/ytp/youtube-url/save", {
    pid,
    uid,
    email,
    round,
    r_status,
    r_link,
    provider,
  });
  const result = response.data?.data ?? response.data;
  const savedRound = Number(result?.round);
  const savedStatus = Number(result?.r_status);
  const savedEmail = typeof result?.email === "string" ? result.email : email;
  const savedProvider = typeof result?.provider === "string" ? result.provider : provider;
  const savedLink = typeof result?.r_link === "string" ? result.r_link : r_link;
  if (
    !Number.isInteger(savedRound) ||
    savedRound < 1 ||
    savedRound > 12 ||
    !Number.isInteger(savedStatus) ||
    savedStatus < 1 ||
    (savedStatus === 2 && !savedEmail.trim())
  ) {
    throw new Error("유튜브 URL 저장 응답에 회차, 회차상태 또는 계정이 없습니다.");
  }
  return { round: savedRound, r_status: savedStatus, email: savedEmail.trim(), r_link: savedLink, provider: savedProvider };
};
